import { BllErrorCode } from "@arbeidsark/shared"
import type { TRPCClientErrorBase } from "@trpc/client"
import type { DefaultErrorShape } from "@trpc/server"
import { useCallback } from "react"
import type { AddToastFn } from "~/context/ToastContext"
import type { ToastType } from "~/ur-react-components"
import { useTranslate } from "~/ur-react-hooks"

const toastTypeLookups: {
  [key: string]: ToastType
} = {
  [BllErrorCode.Unknown]: "error",
  [BllErrorCode.NotFound]: "error",
  [BllErrorCode.BadInput]: "error",
  [BllErrorCode.InvalidCredentials]: "error",
  [BllErrorCode.Unauthorized]: "error",
  [BllErrorCode.Unauthenticated]: "error",
  [BllErrorCode.InternalError]: "error",
  [BllErrorCode.LanguageNotSupported]: "error",
  [BllErrorCode.WordlistTooSmall]: "error",
  [BllErrorCode.ConfigurationNotFound]: "error",
  [BllErrorCode.NoAllowedModules]: "error",
  [BllErrorCode.LanguageCategoryNotDefined]: "error",
  [BllErrorCode.MathematicsNotAllowedForWorlistDeduction]: "error",
  [BllErrorCode.CrosswordComputationTimedOut]: "error",
  [BllErrorCode.CrosswordComputationFailed]: "error",
  [BllErrorCode.CrosswordWordTooLarge]: "error",
  [BllErrorCode.WordSearchComputationTimedOut]: "error",
  [BllErrorCode.WordSearchComputationFailed]: "error",
  [BllErrorCode.WordSearchWordTooLarge]: "error",
  [BllErrorCode.ReadingTextOnlyFullLayout]: "error",
  [BllErrorCode.ReadingTextsNoDataAvailable]: "error",
  [BllErrorCode.ReadingTextsNoContentAvailable]: "error",
  [BllErrorCode.DivisionCalculationTimedOut]: "error",
  [BllErrorCode.DivisonLayoutUndeterminable]: "error",
  [BllErrorCode.DivisionCannotFitHalfPageGridSize]: "error",
  [BllErrorCode.EmailAlreadyTaken]: "error",
  [BllErrorCode.IncorrectPassword]: "error",
  [BllErrorCode.OrganizationNameAlreadyTaken]: "error",
  [BllErrorCode.RemoteStripeError]: "error",
  [BllErrorCode.StripeCustomerNotFound]: "error",
}

export function useErrorHandling() {
  const translations = useTranslate({
    defaultErrorMessage: "error.generic",
    [BllErrorCode.Unknown]: "errors.from-code.unknown",
    [BllErrorCode.NotFound]: "errors.from-code.not-found",
    [BllErrorCode.BadInput]: "errors.from-code.bad-input",
    [BllErrorCode.InvalidCredentials]: "errors.from-code.invalid-credentials",
    [BllErrorCode.Unauthorized]: "errors.from-code.unauthorized",
    [BllErrorCode.Unauthenticated]: "errors.from-code.unauthenticated",
    [BllErrorCode.InternalError]: "errors.from-code.internal-error",
    [BllErrorCode.LanguageNotSupported]:
      "errors.from-code.language-not-supported",
    [BllErrorCode.WordlistTooSmall]: "errors.from-code.wordlist-too-small",
    [BllErrorCode.ConfigurationNotFound]:
      "errors.from-code.configuration-not-found",
    [BllErrorCode.NoAllowedModules]: "errors.from-code.no-allowed-modules",
    [BllErrorCode.LanguageCategoryNotDefined]:
      "errors.from-code.language-category-not-defined",
    [BllErrorCode.MathematicsNotAllowedForWorlistDeduction]:
      "errors.from-code.mathematics-not-allowed-for-wordlist-deduction",
    [BllErrorCode.CrosswordComputationTimedOut]:
      "errors.from-code.crossword-computation-timed-out",
    [BllErrorCode.CrosswordComputationFailed]:
      "errors.from-code.crossword-computation-failed",
    [BllErrorCode.CrosswordWordTooLarge]:
      "errors.from-code.crossword-word-too-large",
    [BllErrorCode.WordSearchComputationTimedOut]:
      "errors.from-code.wordsearch-computation-timed-out",
    [BllErrorCode.WordSearchComputationFailed]:
      "errors.from-code.wordsearch-computation-failed",
    [BllErrorCode.WordSearchWordTooLarge]:
      "errors.from-code.wordsearch-word-too-large",
    [BllErrorCode.ReadingTextOnlyFullLayout]:
      "errors.from-code.readingtext-only-full-layout",
    [BllErrorCode.ReadingTextsNoDataAvailable]:
      "errors.from-code.readingtexts-no-data-available",
    [BllErrorCode.ReadingTextsNoContentAvailable]:
      "errors.from-code.readingtexts-no-content-available",
    [BllErrorCode.DivisionCalculationTimedOut]:
      "errors.from-code.division-calculation-timed-out",
    [BllErrorCode.DivisonLayoutUndeterminable]:
      "errors.from-code.division-layout-undeterminable",
    [BllErrorCode.DivisionCannotFitHalfPageGridSize]:
      "errors.from-code.division-cannot-fit-half-page-grid-size",
    [BllErrorCode.EmailAlreadyTaken]: "errors.from-code.email-already-taken",
    [BllErrorCode.IncorrectPassword]: "errors.from-code.incorrect-password",
    [BllErrorCode.OrganizationNameAlreadyTaken]:
      "errors.from-code.organization-name-already-taken",
    [BllErrorCode.RemoteStripeError]: "errors.from-code.remote-stripe-error",
    [BllErrorCode.StripeCustomerNotFound]:
      "errors.from-code.stripe-customer-not-found",
  })

  const getDisplayMessage = useCallback(
    (errorMessage: string, _tprcCode?: string) => {
      // Try to extract the error type from the formatted error message
      const errorTypeMatch = errorMessage.match(/\[Type\]: ([^\n]+)/)
      const errorType = errorTypeMatch ? errorTypeMatch[1].trim() : errorMessage
      
      const translation =
        errorType in translations
          ? // Some ugly ts-hacking to make sure we get the correct type
            (translations[errorType as keyof typeof translations] as string)
          : translations.defaultErrorMessage

      return translation
    },
    [translations],
  )

  const getToastInfo = useCallback(
    (
      errorMessage: string,
      trpcCode?: string,
      fallbackErrorType?: ToastType,
    ) => {
      // Try to extract the error type from the formatted error message
      const errorTypeMatch = errorMessage.match(/\[Type\]: ([^\n]+)/)
      const errorType = errorTypeMatch ? errorTypeMatch[1].trim() : errorMessage
      
      const displayMessage = getDisplayMessage(errorMessage, trpcCode)
      const toastType =
        toastTypeLookups[errorType] ?? fallbackErrorType ?? "info"

      return {
        type: toastType,
        message: displayMessage,
      }
    },
    [getDisplayMessage],
  )

  const trpcErrorHandler = useCallback(
    (toast: AddToastFn, defaultErrorType: ToastType = "error") =>
      (error: TRPCClientErrorBase<DefaultErrorShape>) => {
        const toastInfo = getToastInfo(
          error.message,
          error.data?.code,
          defaultErrorType,
        )
        toast(toastInfo.type, toastInfo.message)
      },
    [getToastInfo],
  )

  return {
    getDisplayMessage,
    getToastInfo,
    trpcErrorHandler,
  }
}
